import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Sparkles, ArrowRight, X, Tag, BookOpen, Zap,
    Scroll, RefreshCcw, Globe, Image as ImageIcon,
    BarChart2, CheckCircle, Shield, PlayCircle,
    Copy, Download, Link, FileText, FileCode, Loader2,
    Settings2, Undo2, Redo2, ChevronDown, ChevronRight,
    Search, Cpu, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

// ─── Real pipeline phases (from phase_definitions.js) ─────────────────────────
const REAL_PHASES = [
    { id: 'intake',       name: 'Intake & Discovery',    desc: 'Phase 1: Multi-Mode Intake',      icon: Search },
    { id: 'research',     name: 'Parallel Research',     desc: 'Phase 2: Grounding Pull',          icon: Zap,         iconColor: 'text-amber-500' },
    { id: 'fingerprint',  name: 'Digital Fingerprinting',desc: 'Phase 3: Sacred Data Registry',   icon: Shield },
    { id: 'drafting',     name: 'Foundation Drafting',   desc: 'Phase 4: Dual Engine',             icon: FileText },
    { id: 'fusion',       name: 'Narrative Fusion',      desc: 'Phase 5: Sequential Merge',        icon: Cpu,         iconColor: 'text-indigo-500' },
    { id: 'qa',           name: 'QA & Adaptation',       desc: 'Phase 6: Vibe-Shield Audit',       icon: Sparkles,    iconColor: 'text-emerald-500' },
    { id: 'production',   name: 'Production Export',     desc: 'Phase 7: Final Completion',        icon: CheckCircle },
];

// ─── Real categories (exact names from categories.js) ─────────────────────────
const DEMO_CATEGORY = 'Mystery, True Crime & Dark Content';
const DEMO_SUBCATEGORIES = [
    'True Crime (Solved)',
    'True Crime (Unsolved)',
    'Historical Mysteries',
    'Lost Civilizations',
    'Paranormal Stories',
    'UFOs & UAPs',
    'Conspiracy Theories (Neutral Tone)',
    'Strange Historical Events',
];
const DEMO_SUBCATEGORY = 'Historical Mysteries';

// A sampling of real categories for the grid mock
const REAL_CATEGORIES_SAMPLE = [
    { name: 'Entertainment & Pop Culture', count: 8 },
    { name: 'Gaming',                      count: 8 },
    { name: 'Education & Explainers',      count: 8 },
    { name: 'Mystery, True Crime & Dark Content', count: 8, active: true },
    { name: 'Lifestyle & Self-Improvement',count: 7 },
    { name: 'Finance & Global Markets',    count: 7 },
];

// ─── Demo script data ──────────────────────────────────────────────────────────
const DEMO_SCRIPTS_META = [
    { title: 'The Lost City of Z — Fact or Fiction?', badge: 'SCRIPT 1' },
    { title: 'The Dyatlov Pass Incident Revisited',   badge: 'SCRIPT 2' },
    { title: 'The Mystery of the Amber Room',         badge: 'SCRIPT 3' },
];
const DEMO_PARAGRAPHS = [
    { id: 'hook',    tag: '[HOOK]',       text: "In 1925, British explorer Percy Fawcett vanished into the Amazon, searching for a city he called 'Z'. He was never seen again. Was it madness? Conspiracy? Or did he find something the world wasn't ready for?" },
    { id: 'context', tag: '[CONTEXT]',   text: "Fawcett was no amateur. A decorated officer, skilled cartographer, and veteran of four Amazon expeditions, he was methodical where others were reckless. His disappearance stunned the world." },
    { id: 'mystery', tag: '[KEY MOMENT]',text: "What happened in those final months? His last letter, sent from camp 'Dead Horse', described impossible ruins and a city of stone. Then silence. Over 100 searchers have since died or vanished trying to find him." },
    { id: 'legacy',  tag: '[LEGACY]',    text: "Nearly a century later, LiDAR satellites have detected vast pre-Columbian settlements beneath the Amazon canopy — settlements that match Fawcett's descriptions almost exactly." },
];
const DEMO_REGEN_TEXT = "↻ A new angle: Fawcett wasn't just searching for ruins — he believed the Amazon held proof that civilization began in South America, not the Middle East. His theory was considered lunacy in 1925. LiDAR data now suggests he may have been right all along.";

// ─── Tone / Attribution data ──────────────────────────────────────────────────
const DEMO_TONES = [
    { v: 'cinematic',     l: 'Cinematic & Dramatic',  desc: 'Vivid, high-stakes storytelling' },
    { v: 'neutral',       l: 'Neutral / Authentic',   desc: 'Balanced, factual, credible' },
    { v: 'conversational',l: 'Conversational',         desc: 'Casual, friendly, relatable' },
    { v: 'academic',      l: 'Academic & Analytical', desc: 'Deep dives with structured insight' },
];
const DEMO_ATTRIBUTIONS = [
    { v: 'none',    l: 'No Attribution' },
    { v: 'channel', l: 'Channel Name' },
    { v: 'name',    l: 'Creator Name' },
];

// ─── Sidebar tools list ───────────────────────────────────────────────────────
const SIDEBAR_TOOLS = [
    { icon: Copy,      label: 'Copy',          desc: 'Copy script to clipboard.',              sub: '' },
    { icon: Download,  label: 'Download',       desc: 'Save .txt with or without visual cues.', sub: '' },
    { icon: Link,      label: 'View Sources',   desc: 'All research references & citations.',   sub: '' },
    { icon: Download,  label: 'SEO Metadata',   desc: 'YouTube / Shorts / TikTok metadata.',    sub: 'youtube' },
    { icon: ImageIcon, label: 'Image Prompts',  desc: 'AI-ready visual prompts per scene.',     sub: '' },
    { icon: FileText,  label: 'Research',       desc: 'Raw data the engine gathered.',          sub: '' },
];

// ─── Tour steps ───────────────────────────────────────────────────────────────
const STEPS = [
    { id: 'welcome',    target: null,                    wide: false, label: 'Welcome' },
    { id: 'category',   target: 'category-grid',         wide: false, label: '1.A Category' },
    { id: 'focus',      target: 'focus-topic',           wide: false, label: 'Focus Topic' },
    { id: 'cta',        target: 'configure-style-btn',   wide: false, label: 'Configure Style' },
    { id: 'style',      target: 'style-grid',            wide: false, label: 'Personalize Style' },
    { id: 'start',      target: 'start-generation-btn',  wide: false, label: 'Start Generation' },
    { id: 'generating', target: null,                    wide: true,  label: 'Generating…', isGeneration: true },
    { id: 'writers',    target: null,                    wide: true,  label: 'Writers Room' },
    { id: 'sidebar',    target: null,                    wide: true,  label: 'Sidebar Tools' },
    { id: 'done',       target: null,                    wide: false, label: 'Done!' },
];
const TOTAL = STEPS.length;

// ─── Geometry ─────────────────────────────────────────────────────────────────
const PAD = 12;
const NARROW_W = Math.min(window.innerWidth - 32, 390);
const WIDE_W = Math.min(window.innerWidth - 48, 880);

function getRect(target) {
    if (!target) return null;
    const el = document.querySelector(`[data-tutorial-target="${target}"]`);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2, bottom: r.bottom + PAD, right: r.right + PAD, cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
}

function calcStyle(_rect, wide) {
    const W = wide ? WIDE_W : NARROW_W;
    // Always center the card — spotlight still glows on the real element behind
    return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: W };
}

// ─── SVG Spotlight ────────────────────────────────────────────────────────────
const SpotlightMask = ({ rect }) => {
    if (!rect) return <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" style={{ zIndex: 190 }} />;
    return (
        <svg className="fixed inset-0 pointer-events-none" style={{ zIndex: 190, width: '100vw', height: '100vh' }}>
            <defs><mask id="sm"><rect width="100%" height="100%" fill="white" /><rect x={rect.left} y={rect.top} width={rect.width} height={rect.height} rx={14} fill="black" /></mask></defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.76)" mask="url(#sm)" />
            <rect x={rect.left} y={rect.top} width={rect.width} height={rect.height} rx={14} fill="none" stroke="hsl(var(--premium))" strokeWidth={2.5} strokeDasharray="8 4" opacity={0.9} />
        </svg>
    );
};

// ─── Step Bodies ──────────────────────────────────────────────────────────────

const WelcomeBody = () => (
    <div className="flex flex-col items-center text-center py-2">
        <div className="w-14 h-14 rounded-2xl bg-premium/10 flex items-center justify-center mb-4 ring-4 ring-premium/10">
            <Sparkles className="w-7 h-7 text-premium animate-pulse" />
        </div>
        <h2 className="text-xl font-black tracking-tight mb-2">Welcome to EndoCore</h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            I'm the <strong className="text-foreground">Niche Content Navigator</strong> — I turn any topic into a high-performance video script in seconds.
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">
            This tour uses <strong className="text-foreground">Sandbox Mode</strong> — no credits used. I'll spotlight the real UI at each step so you know exactly where to click.
        </p>
    </div>
);

const CategoryBody = () => (
    <div>
        <h3 className="font-black text-base mb-1">1.A Select Primary Category</h3>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            Click the tile that matches your content niche. For this demo, we'll pick <strong className="text-foreground">Mystery, True Crime & Dark Content</strong>.
        </p>
        <div className="border border-border rounded-2xl p-3 bg-muted/20">
            <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-2">1.A Select Primary Category</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {REAL_CATEGORIES_SAMPLE.map((c) => (
                    <div key={c.name} className={cn(
                        "p-3 rounded-xl border text-xs font-bold",
                        c.active ? "bg-premium/10 border-premium/40 text-premium ring-2 ring-premium/20 shadow-sm shadow-premium/10" : "bg-white/5 border-white/10 text-foreground"
                    )}>
                        {c.name}
                        <p className="text-[9px] font-black opacity-50 mt-0.5">{c.count} TOPICS</p>
                    </div>
                ))}
            </div>
            <p className="text-[10px] text-premium mt-2 text-center font-semibold">↑ These are the real categories — highlighted on screen behind this card</p>
        </div>
    </div>
);

const FocusBody = () => (
    <div>
        <h3 className="font-black text-base mb-1">2 & 3: Subcategory + Focus Topic</h3>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            After selecting the primary category, choose a subcategory. Then optionally pick a <strong className="text-foreground">Custom Theme</strong> to pin down a specific angle.
        </p>
        <div className="border border-border rounded-2xl p-3 bg-muted/20 space-y-3">
            <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1.5">2. Select Subcategory</p>
                <div className="flex items-center justify-between border border-border rounded-xl px-3 h-10 bg-background text-sm font-bold">
                    <span className="text-premium">{DEMO_SUBCATEGORY}</span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="mt-1.5 border border-border rounded-xl overflow-hidden bg-background">
                    {DEMO_SUBCATEGORIES.slice(0, 4).map((sub) => (
                        <div key={sub} className={cn("px-3 py-2 text-xs font-semibold border-b border-border/50 last:border-0",
                            sub === DEMO_SUBCATEGORY ? "bg-premium/10 text-premium font-bold" : "text-muted-foreground"
                        )}>
                            {sub === DEMO_SUBCATEGORY && <span className="mr-1.5">✓</span>}{sub}
                        </div>
                    ))}
                </div>
            </div>
            <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1.5">3. Focus Topic (Optional)</p>
                <div className="flex gap-2">
                    <div className="px-3 py-1.5 rounded-lg text-xs font-bold bg-foreground text-background">General</div>
                    <div className="px-3 py-1.5 rounded-lg text-xs font-bold border border-border text-muted-foreground flex items-center gap-1.5">
                        <Shield className="w-3 h-3" /> Custom Theme
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const CTABody = () => (
    <div>
        <h3 className="font-black text-base mb-1">Configure Style →</h3>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            Once your category is set, this button activates. Press it to move to <strong className="text-foreground">Step 2: Personalize Style</strong> — where you set tone and voice.
        </p>
        <div className="border border-border rounded-2xl p-4 bg-muted/20 flex justify-end">
            <div className="h-12 px-8 rounded-2xl bg-premium text-white font-black text-sm flex items-center gap-2 shadow-lg shadow-premium/20 opacity-90">
                Configure Style <ChevronRight className="w-5 h-5" />
            </div>
        </div>
        <p className="text-[10px] text-premium mt-2 text-center font-semibold">↑ The real button is highlighted behind this card</p>
    </div>
);

const StyleBody = () => {
    const [selTone, setSelTone] = useState(0);
    const [selAttr, setSelAttr] = useState(0);
    return (
        <div>
            <h3 className="font-black text-base mb-1">Personalize Style</h3>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                Choose how your script sounds and how you're credited. These preferences are saved to your profile automatically.
            </p>
            <div className="border border-border rounded-2xl bg-card/60 overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="font-black text-sm">Personalize Style</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">Step 2 of 2</span>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Tone & Narrative Style</label>
                        <div className="rounded-xl border border-border bg-background overflow-hidden">
                            {DEMO_TONES.map((t, i) => (
                                <button key={t.v} onClick={() => setSelTone(i)}
                                    className={cn("w-full flex items-start gap-2 px-3 py-2 text-left transition-colors",
                                        selTone === i ? "bg-premium/10" : "hover:bg-muted/30"
                                    )}>
                                    <div className={cn("w-3 h-3 rounded-full mt-0.5 border shrink-0",
                                        selTone === i ? "bg-premium border-premium" : "border-border")} />
                                    <div>
                                        <p className={cn("text-[11px] font-bold", selTone === i ? "text-premium" : "text-foreground")}>{t.l}</p>
                                        <p className="text-[10px] text-muted-foreground">{t.desc}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Mention in Script</label>
                        <div className="rounded-xl border border-border bg-background overflow-hidden">
                            {DEMO_ATTRIBUTIONS.map((a, i) => (
                                <button key={a.v} onClick={() => setSelAttr(i)}
                                    className={cn("w-full flex items-center gap-2 px-3 py-3 text-left transition-colors",
                                        selAttr === i ? "bg-premium/10" : "hover:bg-muted/30"
                                    )}>
                                    <div className={cn("w-3 h-3 rounded-full border shrink-0",
                                        selAttr === i ? "bg-premium border-premium" : "border-border")} />
                                    <span className={cn("text-[11px] font-bold", selAttr === i ? "text-premium" : "text-foreground")}>{a.l}</span>
                                </button>
                            ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground italic px-1">Saved to your profile automatically.</p>
                    </div>
                </div>
            </div>
            <p className="text-[10px] text-premium mt-2 text-center font-semibold">↑ The real Tone & Attribution selectors are highlighted behind this card</p>
        </div>
    );
};

const StartBody = () => (
    <div>
        <h3 className="font-black text-base mb-2 flex items-center gap-2">
            <Zap className="w-4 h-4 text-premium" /> Start Content Generation
        </h3>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            Press this to fire the 7-phase EndoCore pipeline. Research, scripting, QA, and SEO are all handled automatically.
        </p>
        <div className="border border-border rounded-2xl p-4 bg-muted/20">
            <div className="w-full h-16 rounded-2xl bg-premium text-white font-black text-lg flex items-center justify-center gap-3 relative overflow-hidden shadow-xl shadow-premium/20">
                <div className="absolute inset-0 bg-white/5" />
                Start Content Generation
                <Zap className="w-6 h-6 fill-current animate-pulse" />
            </div>
            <p className="text-[11px] text-muted-foreground text-center mt-2 italic">Typical time: <strong className="text-foreground">15–45 seconds</strong></p>
        </div>
        <p className="text-[10px] text-premium mt-2 text-center font-semibold">↑ The real button is highlighted behind this card</p>
    </div>
);

const GenerationBody = ({ onAuto }) => {
    const [phaseIdx, setPhaseIdx] = useState(-1);
    const [done, setDone] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        let i = 0;
        const tick = () => {
            if (i < REAL_PHASES.length) {
                setPhaseIdx(i);
                setProgress(Math.round(((i + 1) / REAL_PHASES.length) * 100));
                i++;
                setTimeout(tick, 500);
            } else {
                setDone(true);
                setProgress(100);
                setTimeout(onAuto, 900);
            }
        };
        setTimeout(tick, 400);
    }, []); // eslint-disable-line

    return (
        <div className="space-y-4">
            {/* Top header card — mirrors real ProcessingView progress card */}
            <div className="rounded-2xl border border-premium/20 bg-premium/5 p-4">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className={cn("text-lg font-bold tracking-tight", done ? "text-green-400" : "text-premium")}>
                            {done ? 'Processing Complete' : 'System Processing…'}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                            {done ? 'Your content is ready in the Writers Room.' : 'EndoCore Engine is hard at work on your content.'}
                        </p>
                    </div>
                    <span className="text-3xl font-black text-premium drop-shadow-sm tabular-nums">{progress}%</span>
                </div>
                <div className="relative h-3.5 w-full bg-white/10 rounded-full overflow-hidden border border-white/5 shadow-inner">
                    <motion.div
                        className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${done ? 'bg-green-400' : 'bg-premium shadow-glow'}`}
                        animate={{ width: `${progress}%` }}
                        transition={{ ease: 'easeOut', duration: 0.6 }}
                    />
                </div>
            </div>

            {/* 2-column layout like the real 1/3 + 2/3 grid — simplified */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Pipeline Status — matches real phase list card */}
                <div className="rounded-2xl border border-border bg-card/60 overflow-hidden">
                    <div className="px-4 py-3 border-b border-border">
                        <p className="text-sm font-bold">Pipeline Status</p>
                    </div>
                    <div className="divide-y divide-border/40">
                        {REAL_PHASES.map((phase, idx) => {
                            const isComplete = done || idx < phaseIdx;
                            const isActive = !done && idx === phaseIdx;
                            const PhaseIcon = phase.icon;
                            return (
                                <div key={phase.id} className={cn("flex items-center gap-3 px-4 py-3 transition-colors", isActive && "bg-primary/5", isComplete && "opacity-50")}>
                                    <div className={cn("w-7 h-7 rounded-full flex items-center justify-center border-2 shrink-0 transition-all",
                                        isComplete ? "bg-green-500 border-green-500 text-white" :
                                        isActive ? "bg-primary border-primary text-primary-foreground animate-pulse shadow-sm shadow-primary/30" :
                                        "bg-muted border-border text-muted-foreground"
                                    )}>
                                        {isComplete ? <CheckCircle className="w-3.5 h-3.5" /> : <PhaseIcon className={cn("w-3.5 h-3.5", phase.iconColor)} />}
                                    </div>
                                    <div className="min-w-0">
                                        <p className={cn("text-[clamp(0.875rem,3vw,1rem)] font-bold break-words whitespace-normal leading-tight mb-0.5", isActive ? "text-primary" : "text-foreground")}>{phase.name}</p>
                                        <p className="text-[0.625rem] sm:text-[0.7rem] text-muted-foreground break-words whitespace-normal uppercase tracking-tight">{phase.desc}</p>
                                    </div>
                                    {isActive && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary ml-auto shrink-0" />}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Activity area — shows script cards when done */}
                <div className="rounded-2xl border border-border bg-card/60 overflow-hidden">
                    <div className="px-4 py-3 border-b border-border">
                        <p className="text-sm font-bold">Activity</p>
                    </div>
                    {done ? (
                        <div className="p-3 space-y-2">
                            {DEMO_SCRIPTS_META.map((s, i) => (
                                <div key={i} className="p-3 rounded-xl border border-border bg-card/60 hover:border-premium/30 transition-all group cursor-pointer">
                                    <div className="flex items-center justify-between gap-2">
                                        <div>
                                            <Badge className="bg-premium text-white text-[9px] mb-1">Script {i + 1}</Badge>
                                            <p className="text-[11px] font-bold leading-snug">{s.title}</p>
                                        </div>
                                        <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 text-primary transition-opacity shrink-0" />
                                    </div>
                                    <div className="flex gap-1.5 mt-2 flex-wrap">
                                        <div className="text-[9px] font-bold px-2 py-1 rounded-lg bg-premium/10 text-premium">Open in Writers Room</div>
                                        <div className="text-[9px] font-bold px-2 py-1 rounded-lg border border-border text-muted-foreground">View Sources</div>
                                        <div className="text-[9px] font-bold px-2 py-1 rounded-lg border border-border text-muted-foreground">SEO Metadata</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-48 gap-3">
                            <div className="w-10 h-10 rounded-full border-2 border-premium/30 border-t-premium animate-spin" />
                            <p className="text-xs text-muted-foreground">Pipeline running…</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const WritersRoomBody = () => {
    const [regenId, setRegenId] = useState(null);
    const [regenDone, setRegenDone] = useState(null);
    const [activeScript, setActiveScript] = useState(0);

    const handleRegen = (id) => {
        if (regenId) return;
        setRegenId(id);
        setTimeout(() => { setRegenDone(id); setRegenId(null); }, 1400);
    };

    return (
        <div className="space-y-3">
            <div>
                <h3 className="font-black text-base">Writers Room</h3>
                <p className="text-xs text-muted-foreground">Your completed scripts land here. Here's what the real interface looks like.</p>
            </div>
            <div className="grid grid-cols-[160px_1fr_160px] gap-2 rounded-2xl border border-border overflow-hidden bg-background/40" style={{ height: 360 }}>

                {/* LEFT: Script Navigator */}
                <div className="flex flex-col border-r border-white/5 overflow-hidden">
                    <div className="px-3 py-2 border-b border-white/5 shrink-0">
                        <p className="text-[8px] font-black uppercase tracking-widest text-muted-foreground">Generated Scripts</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                        {DEMO_SCRIPTS_META.map((s, i) => (
                            <button key={i} onClick={() => setActiveScript(i)}
                                className={cn("w-full text-left p-2 rounded-xl border transition-all",
                                    activeScript === i ? "bg-premium/10 border-premium/40" : "bg-white/5 border-white/5 hover:bg-white/10"
                                )}>
                                <div className="flex items-center gap-1 mb-1">
                                    <span className={cn("text-[8px] font-black px-1 py-0.5 rounded", activeScript === i ? "bg-premium text-white" : "bg-white/10")}>{s.badge}</span>
                                    {activeScript === i && <div className="w-1 h-1 rounded-full bg-premium animate-pulse" />}
                                </div>
                                <p className="text-[10px] font-bold line-clamp-2 leading-snug">{s.title}</p>
                            </button>
                        ))}
                    </div>
                    <div className="border-t border-white/5 p-2 space-y-1 shrink-0">
                        <p className="text-[7px] font-black uppercase text-muted-foreground mb-1">Export & Tools</p>
                        {[Copy, Download, FileText].map((Icon, i) => (
                            <div key={i} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-white/5 bg-white/5 text-[9px] text-muted-foreground">
                                <Icon className="w-2.5 h-2.5" />{i === 0 ? 'Copy' : i === 1 ? 'Download' : 'More…'}
                            </div>
                        ))}
                    </div>
                </div>

                {/* CENTER: ScriptViewer */}
                <div className="flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 shrink-0">
                        <div className="flex gap-1 bg-white/5 p-0.5 rounded-lg border border-white/10">
                            <div className="px-2.5 py-1 rounded-md bg-premium text-white text-[8px] font-black uppercase">Long Form</div>
                            <div className="px-2.5 py-1 text-[8px] font-black uppercase text-muted-foreground">Short Form</div>
                        </div>
                        <div className="flex items-center gap-1 px-1.5 py-1 rounded-full bg-white/5 border border-white/5">
                            <Settings2 className="w-2.5 h-2.5 opacity-40" />
                            <div className="w-5 h-2.5 rounded-full bg-premium relative"><div className="w-2 h-2 rounded-full bg-white absolute top-0.25 right-0.25" /></div>
                            <span className="text-[8px] font-black uppercase opacity-40">Cues</span>
                        </div>
                    </div>
                    <div className="px-3 py-1.5 border-b border-white/5 shrink-0">
                        <p className="text-[clamp(0.7rem,2vw,0.8rem)] font-bold italic text-muted-foreground text-center break-words whitespace-normal px-2 leading-tight">{DEMO_SCRIPTS_META[activeScript].title}</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {DEMO_PARAGRAPHS.map((para) => (
                            <div key={para.id} className={cn("p-2.5 rounded-xl border transition-all",
                                regenDone === para.id ? "border-premium/30 bg-premium/5" : "border-white/5 bg-white/5"
                            )}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-[8px] font-black bg-white/5 border border-white/10 px-1.5 py-0.5 rounded text-muted-foreground">{para.tag}</span>
                                    <button onClick={() => handleRegen(para.id)} disabled={!!regenId}
                                        className="flex items-center gap-1 text-[8px] font-black text-muted-foreground hover:text-premium transition-colors disabled:opacity-30">
                                        {regenId === para.id ? <><RefreshCcw className="w-2 h-2 animate-spin" /> Rewriting…</>
                                            : regenDone === para.id ? <><CheckCircle className="w-2 h-2 text-green-400" /> Rewritten</>
                                            : <><RefreshCcw className="w-2 h-2" /> Regen</>}
                                    </button>
                                </div>
                                <p className={cn("text-[10px] leading-relaxed", regenDone === para.id ? "text-premium/80" : "text-foreground/80")}>
                                    {regenDone === para.id ? <em>{DEMO_REGEN_TEXT}</em> : para.text}
                                </p>
                            </div>
                        ))}
                    </div>
                    <div className="px-3 py-1.5 bg-white/5 border-t border-white/5 flex justify-between shrink-0">
                        <span className="text-[8px] opacity-25">Highlight text for refinement tools…</span>
                        <span className="text-[8px] font-black opacity-25 tabular-nums">3,217 CHARS</span>
                    </div>
                </div>

                {/* RIGHT: Control Center */}
                <div className="flex flex-col border-l border-white/5 overflow-hidden">
                    <div className="px-3 py-2 border-b border-white/5 bg-white/5 shrink-0">
                        <p className="text-[8px] font-black uppercase tracking-widest text-muted-foreground">Control Center</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                        <div>
                            <p className="text-[8px] font-black uppercase text-muted-foreground mb-1">Rewrite Tone Preset</p>
                            <div className="flex items-center justify-between border border-border rounded-lg px-2 py-1.5 bg-background/50">
                                <span className="text-[10px] font-semibold">Cinematic</span>
                                <ChevronDown className="w-3 h-3 text-muted-foreground" />
                            </div>
                        </div>
                        <div>
                            <p className="text-[8px] font-black uppercase text-muted-foreground mb-1">Rewrite Notes</p>
                            <div className="rounded-lg border border-border bg-background/50 px-2 py-2 min-h-[44px] text-[9px] text-muted-foreground italic">e.g. "More dramatic…"</div>
                        </div>
                        <button className="w-full bg-premium text-white font-black rounded-xl py-2 text-[9px] flex items-center justify-center gap-1.5">
                            <RefreshCcw className="w-3 h-3" /> Rewrite Script
                        </button>
                        <div className="flex gap-1">
                            <button className="flex-1 flex items-center justify-center gap-1 border border-border rounded-lg py-1.5 text-[8px] font-bold text-muted-foreground">
                                <Undo2 className="w-2.5 h-2.5" /> Undo
                            </button>
                            <button className="flex-1 flex items-center justify-center gap-1 border border-border rounded-lg py-1.5 text-[8px] font-bold text-muted-foreground">
                                <Redo2 className="w-2.5 h-2.5" /> Redo
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <p className="text-[10px] text-muted-foreground text-center italic">↺ Try the Regen button on any paragraph — it's interactive!</p>
        </div>
    );
};

const SidebarBody = () => {
    const [active, setActive] = useState(0);
    useEffect(() => { const t = setInterval(() => setActive(p => (p + 1) % SIDEBAR_TOOLS.length), 1100); return () => clearInterval(t); }, []);
    return (
        <div className="space-y-3">
            <div>
                <h3 className="font-black text-base">Export & Tools Panel</h3>
                <p className="text-xs text-muted-foreground">Every script comes with these download and export options. Click any tool below to learn what it does.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4">
                <div className="rounded-2xl border border-white/10 bg-background/60 overflow-hidden flex flex-col">
                    <div className="px-3 py-2 border-b border-white/5 bg-white/5">
                        <p className="text-[8px] font-black uppercase tracking-widest text-muted-foreground">Export & Tools</p>
                    </div>
                    <div className="p-2 space-y-1.5">
                        {SIDEBAR_TOOLS.map(({ icon: Icon, label, sub }, i) => (
                            <button key={label} onClick={() => setActive(i)} className={cn(
                                "w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-[11px] font-bold transition-all duration-300",
                                active === i ? "bg-premium/10 border-premium/30 text-premium" : "bg-white/5 border-white/5 text-muted-foreground hover:bg-white/10"
                            )}>
                                <span className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 shrink-0" />{label}</span>
                                {sub && <span className="text-[9px] opacity-50 uppercase">{sub}</span>}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex flex-col gap-3">
                    <AnimatePresence mode="wait">
                        <motion.div key={active} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
                            className="bg-premium/5 border border-premium/20 rounded-2xl p-4">
                            {React.createElement(SIDEBAR_TOOLS[active].icon, { className: "w-5 h-5 text-premium mb-2" })}
                            <p className="font-black text-sm text-premium mb-1">{SIDEBAR_TOOLS[active].label}</p>
                            <p className="text-xs text-muted-foreground leading-relaxed">{SIDEBAR_TOOLS[active].desc}</p>
                        </motion.div>
                    </AnimatePresence>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {SIDEBAR_TOOLS.map(({ icon: Icon, label }, i) => (
                            <button key={label} onClick={() => setActive(i)} className={cn(
                                "flex items-center gap-2 px-2.5 py-2 rounded-xl border text-[10px] font-bold transition-all",
                                active === i ? "bg-premium/10 border-premium/30 text-premium" : "border-border text-muted-foreground hover:bg-muted/30"
                            )}><Icon className="w-3 h-3 shrink-0" />{label}</button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const CompleteBody = () => (
    <div className="flex flex-col items-center text-center py-2">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="w-14 h-14 rounded-2xl bg-green-500/10 flex items-center justify-center mb-4 ring-4 ring-green-500/10">
            <CheckCircle className="w-7 h-7 text-green-400" />
        </motion.div>
        <h2 className="text-xl font-black tracking-tight mb-2">You're All Set!</h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Now you know the full workflow. Your first real generation completes in <strong className="text-foreground">15–45 seconds</strong>.
        </p>
        <div className="space-y-2 text-left w-full">
            {[
                { icon: PlayCircle, text: 'Pick a category above → Configure Style' },
                { icon: Scroll,     text: 'Script lands in the Writers Room when done' },
                { icon: Shield,     text: 'Reset this tour: Settings → Account' },
            ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon className="w-3.5 h-3.5 text-premium shrink-0" />{text}
                </div>
            ))}
        </div>
    </div>
);

// ─── Body router ──────────────────────────────────────────────────────────────
const BODIES = {
    welcome:    (p) => <WelcomeBody {...p} />,
    category:   (p) => <CategoryBody {...p} />,
    focus:      (p) => <FocusBody {...p} />,
    cta:        (p) => <CTABody {...p} />,
    style:      (p) => <StyleBody {...p} />,
    start:      (p) => <StartBody {...p} />,
    generating: (p) => <GenerationBody {...p} />,
    writers:    (p) => <WritersRoomBody {...p} />,
    sidebar:    (p) => <SidebarBody {...p} />,
    done:       (p) => <CompleteBody {...p} />,
};

// ─── Main Component ────────────────────────────────────────────────────────────
export const OnboardingTour = ({ onComplete }) => {
    const [step, setStep] = useState(0);
    const [visible, setVisible] = useState(false);
    const [rect, setRect] = useState(null);
    const [tooltipStyle, setTooltipStyle] = useState({});
    const [autoAdvance, setAutoAdvance] = useState(false);
    const raf = useRef(null);

    const current = STEPS[step];
    const isWide = !!current.wide;

    const measure = useCallback(() => {
        const r = current.target ? getRect(current.target) : null;
        setRect(r);
        setTooltipStyle(calcStyle(r, isWide));
    }, [current, isWide]);

    useEffect(() => {
        const t = setTimeout(() => { setVisible(true); measure(); }, 200);
        return () => clearTimeout(t);
    }, []); // eslint-disable-line

    useEffect(() => {
        measure();
        if (current.target) {
            const el = document.querySelector(`[data-tutorial-target="${current.target}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        cancelAnimationFrame(raf.current);
        const loop = () => { measure(); raf.current = requestAnimationFrame(loop); };
        raf.current = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf.current);
    }, [step]); // eslint-disable-line

    const handleComplete = () => { setVisible(false); setTimeout(onComplete, 300); };
    const handleNext = () => { if (step < TOTAL - 1) { setAutoAdvance(false); setStep(s => s + 1); } else handleComplete(); };
    const handleBack = () => { if (step > 0) { setAutoAdvance(false); setStep(s => s - 1); } };

    if (!visible) return null;

    const Body = BODIES[current.id];
    const isGenerating = current.isGeneration && !autoAdvance;

    return (
        <>
            <AnimatePresence>
                <motion.div key={`bd-${step}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }} style={{ zIndex: 190 }} className="fixed inset-0 pointer-events-none">
                    <SpotlightMask rect={rect} />
                </motion.div>
            </AnimatePresence>

            <div style={{ zIndex: 191 }} className="fixed inset-0 pointer-events-auto" onClick={e => e.stopPropagation()} />

            {/* Centering wrapper — flexbox so Framer Motion transform never fights translate(-50%,-50%) */}
            <div
                style={{ zIndex: 212, maxHeight: 'calc(100vh - 32px)', width: isWide ? WIDE_W : NARROW_W }}
                className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto flex flex-col"
            >
                <AnimatePresence mode="wait">
                    <motion.div key={`tt-${step}`}
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.97 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        style={{ maxHeight: 'calc(100vh - 32px)' }}
                        className="flex flex-col overflow-hidden rounded-2xl bg-card border border-border shadow-2xl shadow-black/50 relative"
                    >
                        {/* Top progress bar */}
                        <div className="h-0.5 bg-muted shrink-0">
                            <motion.div className="h-full bg-premium" animate={{ width: `${((step + 1) / TOTAL) * 100}%` }} transition={{ type: 'spring', stiffness: 80, damping: 20 }} />
                        </div>

                        {/* Skip */}
                        <button onClick={handleComplete} title="Skip tour" className="absolute top-3 right-3 p-1.5 rounded-full text-muted-foreground hover:bg-muted transition-colors z-10">
                            <X className="w-3.5 h-3.5" />
                        </button>

                        {/* Header */}
                        <div className="flex items-center gap-2 px-5 pt-4 pb-0 shrink-0">
                            <Badge variant="outline" className="text-[9px] font-black uppercase tracking-widest border-premium/40 text-premium px-1.5 py-0.5">SANDBOX</Badge>
                            <span className="text-[10px] text-muted-foreground font-semibold">{step + 1} / {TOTAL} — {current.label}</span>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
                            {Body && <Body onAuto={() => setAutoAdvance(true)} />}
                        </div>

                        {/* Sticky nav footer */}
                        <div className="px-5 pb-4 pt-3 border-t border-border/40 shrink-0 bg-card">
                            {!isGenerating ? (
                                <div className="flex items-center gap-2">
                                    {step > 0 && <Button variant="ghost" size="sm" onClick={handleBack} className="text-xs shrink-0">← Back</Button>}
                                    <Button size="sm" onClick={handleNext} className="flex-1 bg-premium hover:bg-premium/90 text-white font-bold text-xs group h-9">
                                        {step === TOTAL - 1 ? 'Start Creating!' : autoAdvance ? 'Open Writers Room →' : 'Next'}
                                        {step < TOTAL - 1 && !autoAdvance && <ArrowRight className="w-3 h-3 ml-1.5 group-hover:translate-x-0.5 transition-transform" />}
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-center text-[10px] text-muted-foreground italic">Please wait for the pipeline to complete…</p>
                            )}
                            <div className="flex justify-center gap-1 mt-3">
                                {Array.from({ length: TOTAL }).map((_, i) => (
                                    <div key={i} className={cn("rounded-full transition-all duration-300",
                                        i === step ? "w-3.5 h-1 bg-premium" : i < step ? "w-1 h-1 bg-premium/40" : "w-1 h-1 bg-muted")} />
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>
        </>
    );
};
